import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowLeft, Save, Download, Send, Calendar, Loader2, Check, ChevronDown,
  Undo2, Redo2, Type, Paintbrush, Image, Sparkles, Eye, RefreshCw,
  AlertTriangle, X, Link2, Clock, CheckCircle2
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  PIN_WIDTH, PIN_HEIGHT, SAFE_ZONE, PIN_PALETTES, LAYOUT_STYLES,
  buildTemplateLayers, ANGLE_LABELS
} from '../../data/pinTemplates';
import TemplatePicker from './TemplatePicker';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => ({});

const SCALE = 0.32; // preview scale

// ─── Pin Optimization Score Component ───
const PinScore = ({ pin }) => {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const calculate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/pin-studio/score`, {
        pinterest_title: pin.pinterest_title || '',
        pinterest_description: pin.pinterest_description || '',
        pinterest_alt_text: pin.pinterest_alt_text || '',
        pinterest_keywords: pin.pinterest_keywords || [],
        headline: pin.headline || '',
        cta: pin.cta || '',
        destination_url: pin.destination_url || '',
        board_id: pin.board_id || '',
      }, getAuth());
      setScore(res.data);
    } catch (e) {
      console.error('Score error:', e);
    }
    setLoading(false);
  };

  useEffect(() => { calculate(); }, [pin.pinterest_title, pin.pinterest_description, pin.headline, pin.cta, pin.destination_url, pin.board_id]);

  if (!score) return null;

  const pct = score.percentage;
  const ring = pct >= 80 ? 'text-green-500' : pct >= 50 ? 'text-amber-500' : 'text-red-500';

  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-4" data-testid="pin-score">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative w-10 h-10">
            <svg className="w-10 h-10 -rotate-90" viewBox="0 0 40 40">
              <circle cx="20" cy="20" r="17" fill="none" stroke="#f5f5f4" strokeWidth="3" />
              <circle cx="20" cy="20" r="17" fill="none" className={ring} strokeWidth="3"
                strokeDasharray={`${pct * 1.07} 107`} strokeLinecap="round" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-sans font-bold text-stone-700">
              {pct}
            </span>
          </div>
          <div>
            <p className="font-sans font-semibold text-stone-700 text-xs">Lumera Pin Score</p>
            <p className="font-sans text-[10px] text-stone-400">{pct}/100 poeng</p>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-stone-300 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-3 space-y-1.5 pt-3 border-t border-stone-50">
          {score.breakdown.map((r, i) => (
            <div key={i} className="flex items-start gap-2">
              {r.passed ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
              )}
              <div>
                <p className="font-sans text-[10px] text-stone-600">{r.rule} ({r.score}/{r.max})</p>
                {r.tip && <p className="font-sans text-[10px] text-amber-600 mt-0.5">{r.tip}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Pin Preview Renderer ───
const PinPreview = ({ layers, scale = SCALE }) => {
  const w = PIN_WIDTH * scale;
  const h = PIN_HEIGHT * scale;

  return (
    <div
      className="relative overflow-hidden bg-white shadow-lg"
      style={{ width: w, height: h, borderRadius: 8 * scale }}
      data-testid="pin-preview"
    >
      {/* Safe zone indicator */}
      <div
        className="absolute pointer-events-none border border-dashed border-blue-200/30 z-40"
        style={{
          left: SAFE_ZONE * scale,
          top: SAFE_ZONE * scale,
          width: (PIN_WIDTH - SAFE_ZONE * 2) * scale,
          height: (PIN_HEIGHT - SAFE_ZONE * 2) * scale,
        }}
      />

      {layers.map((layer) => {
        const x = (layer.x || 0) * scale;
        const y = (layer.y || 0) * scale;
        const lw = (layer.w || 0) * scale;
        const lh = (layer.h || 0) * scale;

        if (layer.type === 'rect') {
          return (
            <div
              key={layer.id}
              className="absolute"
              style={{
                left: x, top: y, width: lw, height: lh,
                background: layer.fill || 'transparent',
                borderRadius: (layer.radius || 0) * scale,
                border: layer.stroke ? `${(layer.strokeWidth || 1) * scale}px solid ${layer.stroke}` : 'none',
              }}
            />
          );
        }

        if (layer.type === 'text') {
          return (
            <div
              key={layer.id}
              className="absolute"
              style={{
                left: x, top: y, width: lw,
                fontFamily: layer.font || 'Inter',
                fontSize: (layer.fontSize || 16) * scale,
                fontWeight: layer.fontWeight || '400',
                color: layer.color || '#111',
                textAlign: layer.align || 'left',
                lineHeight: layer.lineHeight || 1.3,
                letterSpacing: layer.letterSpacing ? layer.letterSpacing * scale : undefined,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {layer.text}
            </div>
          );
        }

        if (layer.type === 'button') {
          return (
            <div
              key={layer.id}
              className="absolute flex items-center justify-center"
              style={{
                left: x, top: y, width: lw, height: lh,
                background: layer.fill || '#B49A88',
                borderRadius: (layer.radius || 4) * scale,
                fontFamily: layer.font || 'Inter',
                fontSize: (layer.fontSize || 16) * scale,
                fontWeight: layer.fontWeight || '600',
                color: layer.color || '#fff',
                border: layer.stroke ? `${(layer.strokeWidth || 1) * scale}px solid ${layer.stroke}` : 'none',
              }}
            >
              {layer.text}
            </div>
          );
        }

        return null;
      })}
    </div>
  );
};

// ─── Main Editor ───
const PinEditor = ({ pinId, brandKit, pinterestStatus, onBack, onPublished }) => {
  const [pin, setPin] = useState(null);
  const [layers, setLayers] = useState([]);
  const [selectedLayerId, setSelectedLayerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [palette, setPalette] = useState(PIN_PALETTES[0]);
  const [history, setHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [boards, setBoards] = useState([]);
  const [showPublish, setShowPublish] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [publishError, setPublishError] = useState('');
  const [exporting, setExporting] = useState(false);
  const autoSaveTimer = useRef(null);

  // Load pin data
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/pin-studio/pins/${pinId}`, getAuth());
        setPin(res.data);
        const layoutStyle = res.data.template_id || 'hero_text';
        const p = PIN_PALETTES.find((pp) => pp.id === res.data.design?.palette_id) || PIN_PALETTES[0];
        setPalette(p);
        if (res.data.design?.layers) {
          setLayers(res.data.design.layers);
        } else {
          const defaultLayers = buildTemplateLayers(layoutStyle, p, {
            headline: res.data.headline,
            subheadline: res.data.subheadline,
            cta: res.data.cta,
          });
          // Set logo from brand kit
          if (brandKit?.brand_name) {
            const logoLayer = defaultLayers.find((l) => l.id === 'logo');
            if (logoLayer) logoLayer.text = brandKit.brand_name;
          }
          setLayers(defaultLayers);
        }

        // Fetch boards
        if (pinterestStatus?.connected) {
          const bRes = await axios.get(`${API}/api/pinterest/boards`, getAuth()).catch(() => ({ data: { boards: [] } }));
          setBoards(bRes.data.boards || []);
        }
      } catch (e) {
        console.error('Load pin error:', e);
      }
      setLoading(false);
    };
    load();
  }, [pinId]);

  // Auto-save every 10 seconds
  useEffect(() => {
    if (!pin) return;
    autoSaveTimer.current = setInterval(() => {
      savePin(true);
    }, 10000);
    return () => clearInterval(autoSaveTimer.current);
  }, [pin, layers]);

  const pushHistory = (newLayers) => {
    const newHistory = history.slice(0, historyIdx + 1);
    newHistory.push(JSON.parse(JSON.stringify(newLayers)));
    setHistory(newHistory.slice(-40));
    setHistoryIdx(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIdx > 0) {
      setHistoryIdx(historyIdx - 1);
      setLayers(JSON.parse(JSON.stringify(history[historyIdx - 1])));
    }
  };

  const redo = () => {
    if (historyIdx < history.length - 1) {
      setHistoryIdx(historyIdx + 1);
      setLayers(JSON.parse(JSON.stringify(history[historyIdx + 1])));
    }
  };

  const updateLayer = (layerId, updates) => {
    setLayers((prev) => {
      const next = prev.map((l) => l.id === layerId ? { ...l, ...updates } : l);
      pushHistory(next);
      return next;
    });
  };

  const updatePin = (field, value) => {
    setPin((p) => ({ ...p, [field]: value }));

    // Sync text layers with pin fields
    if (field === 'headline') {
      setLayers((prev) => prev.map((l) => l.id === 'headline' ? { ...l, text: value } : l));
    } else if (field === 'subheadline' || field === 'sub') {
      setLayers((prev) => prev.map((l) => l.id === 'sub' ? { ...l, text: value } : l));
    } else if (field === 'cta') {
      setLayers((prev) => prev.map((l) => l.id === 'cta' ? { ...l, text: value } : l));
    }
  };

  const changePalette = (newPalette) => {
    setPalette(newPalette);
    const content = {
      headline: pin?.headline || '',
      subheadline: pin?.subheadline || '',
      cta: pin?.cta || '',
    };
    const newLayers = buildTemplateLayers(pin?.template_id || 'hero_text', newPalette, content);
    if (brandKit?.brand_name) {
      const logoLayer = newLayers.find((l) => l.id === 'logo');
      if (logoLayer) logoLayer.text = brandKit.brand_name;
    }
    setLayers(newLayers);
    pushHistory(newLayers);
  };

  const changeLayout = (layoutStyle) => {
    const content = {
      headline: pin?.headline || '',
      subheadline: pin?.subheadline || '',
      cta: pin?.cta || '',
    };
    const newLayers = buildTemplateLayers(layoutStyle, palette, content);
    if (brandKit?.brand_name) {
      const logoLayer = newLayers.find((l) => l.id === 'logo');
      if (logoLayer) logoLayer.text = brandKit.brand_name;
    }
    setLayers(newLayers);
    pushHistory(newLayers);
    setPin((p) => ({ ...p, template_id: layoutStyle }));
  };

  const savePin = async (silent = false) => {
    if (!pin) return;
    if (!silent) setSaving(true);
    try {
      await axios.put(`${API}/api/pin-studio/pins/${pin.id}`, {
        headline: pin.headline,
        subheadline: pin.subheadline,
        cta: pin.cta,
        pinterest_title: pin.pinterest_title,
        pinterest_description: pin.pinterest_description,
        pinterest_alt_text: pin.pinterest_alt_text,
        pinterest_keywords: pin.pinterest_keywords,
        destination_url: pin.destination_url,
        board_id: pin.board_id,
        board_name: pin.board_name,
        template_id: pin.template_id,
        design: { palette_id: palette.id, layers },
      }, getAuth());
      setLastSaved(new Date());
    } catch (e) {
      console.error('Save error:', e);
    }
    if (!silent) setSaving(false);
  };

  // Export pin as canvas image
  const exportPin = async () => {
    setExporting(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = PIN_WIDTH;
      canvas.height = PIN_HEIGHT;
      const ctx = canvas.getContext('2d');

      for (const layer of layers) {
        if (layer.type === 'rect') {
          if (layer.fill && layer.fill !== 'transparent') {
            ctx.fillStyle = layer.fill;
            if (layer.radius) {
              roundRect(ctx, layer.x, layer.y, layer.w, layer.h, layer.radius);
              ctx.fill();
            } else {
              ctx.fillRect(layer.x, layer.y, layer.w, layer.h);
            }
          }
          if (layer.stroke) {
            ctx.strokeStyle = layer.stroke;
            ctx.lineWidth = layer.strokeWidth || 1;
            ctx.strokeRect(layer.x, layer.y, layer.w, layer.h);
          }
        } else if (layer.type === 'text' || layer.type === 'button') {
          if (layer.type === 'button' && layer.fill) {
            ctx.fillStyle = layer.fill;
            roundRect(ctx, layer.x, layer.y, layer.w, layer.h, layer.radius || 0);
            ctx.fill();
          }
          ctx.font = `${layer.fontWeight || '400'} ${layer.fontSize || 16}px ${layer.font || 'Inter'}`;
          ctx.fillStyle = layer.color || '#111';
          ctx.textAlign = layer.type === 'button' ? 'center' : (layer.align || 'left');
          ctx.textBaseline = layer.type === 'button' ? 'middle' : 'top';

          const tx = layer.type === 'button' ? layer.x + layer.w / 2 : (layer.align === 'center' ? layer.x + layer.w / 2 : layer.align === 'right' ? layer.x + layer.w : layer.x);
          const ty = layer.type === 'button' ? layer.y + layer.h / 2 : layer.y;

          if (layer.text) {
            const lines = wrapText(ctx, layer.text, layer.w);
            const lh = (layer.fontSize || 16) * (layer.lineHeight || 1.3);
            lines.forEach((line, i) => {
              ctx.fillText(line, tx, ty + i * lh);
            });
          }
        }
      }

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pin-${pin.id.slice(0, 8)}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export error:', e);
    }
    setExporting(false);
  };

  const handlePublish = async (scheduled = false) => {
    if (!pinterestStatus?.connected) { setPublishError('Pinterest not connected'); return; }
    if (!pin.board_id) { setPublishError('Select a board first'); return; }
    if (!pin.headline) { setPublishError('Pin mangler headline'); return; }

    setPublishing(true);
    setPublishError('');

    try {
      // First save
      await savePin(true);

      // Export image to blob
      const canvas = document.createElement('canvas');
      canvas.width = PIN_WIDTH;
      canvas.height = PIN_HEIGHT;
      const ctx = canvas.getContext('2d');
      for (const layer of layers) {
        if (layer.type === 'rect' && layer.fill && layer.fill !== 'transparent') {
          ctx.fillStyle = layer.fill;
          ctx.fillRect(layer.x || 0, layer.y || 0, layer.w || 0, layer.h || 0);
        } else if (layer.type === 'text' || layer.type === 'button') {
          if (layer.type === 'button' && layer.fill) {
            ctx.fillStyle = layer.fill;
            roundRect(ctx, layer.x, layer.y, layer.w, layer.h, layer.radius || 0);
            ctx.fill();
          }
          ctx.font = `${layer.fontWeight || '400'} ${layer.fontSize || 16}px ${layer.font || 'Inter'}`;
          ctx.fillStyle = layer.color || '#111';
          ctx.textAlign = layer.type === 'button' ? 'center' : (layer.align || 'left');
          ctx.textBaseline = layer.type === 'button' ? 'middle' : 'top';
          const tx = layer.type === 'button' ? layer.x + layer.w / 2 : (layer.align === 'center' ? layer.x + layer.w / 2 : layer.x);
          const ty = layer.type === 'button' ? layer.y + layer.h / 2 : layer.y;
          if (layer.text) ctx.fillText(layer.text, tx, ty);
        }
      }
      const imageBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));

      // Upload image to get URL
      const fd = new FormData();
      fd.append('file', imageBlob, 'pin.png');
      const uploadRes = await axios.post(`${API}/api/storage/upload?folder=pins`, fd, {
        ...getAuth(),
        headers: { ...getAuth().headers, 'Content-Type': 'multipart/form-data' },
      });

      // Build destination URL with UTM
      let destUrl = pin.destination_url || '';
      if (destUrl && pin.utm_source) {
        const sep = destUrl.includes('?') ? '&' : '?';
        destUrl += `${sep}utm_source=${pin.utm_source || 'pinterest'}&utm_medium=${pin.utm_medium || 'organic'}`;
        if (pin.utm_campaign) destUrl += `&utm_campaign=${pin.utm_campaign}`;
      }

      if (scheduled && scheduleDate && scheduleTime) {
        // Schedule
        await axios.post(`${API}/api/pinterest/schedule`, {
          title: pin.pinterest_title || pin.headline,
          description: pin.pinterest_description || '',
          board_id: pin.board_id,
          image_url: uploadRes.data.url || `${API}/api/storage/files/${uploadRes.data.file_id}`,
          link: destUrl,
          scheduled_at: `${scheduleDate}T${scheduleTime}:00`,
          alt_text: pin.pinterest_alt_text || '',
        }, getAuth());

        await axios.put(`${API}/api/pin-studio/pins/${pin.id}`, {
          status: 'scheduled',
          scheduled_at: `${scheduleDate}T${scheduleTime}:00`,
        }, getAuth());
        setPin((p) => ({ ...p, status: 'scheduled' }));
      } else {
        // Publish now
        const pubRes = await axios.post(`${API}/api/pinterest/publish`, {
          title: pin.pinterest_title || pin.headline,
          description: pin.pinterest_description || '',
          board_id: pin.board_id,
          image_url: uploadRes.data.url || `${API}/api/storage/files/${uploadRes.data.file_id}`,
          link: destUrl,
          alt_text: pin.pinterest_alt_text || '',
        }, getAuth());

        await axios.put(`${API}/api/pin-studio/pins/${pin.id}`, {
          status: 'published',
          pinterest_pin_id: pubRes.data.pin_id || null,
          pinterest_pin_url: pubRes.data.pin_url || null,
        }, getAuth());
        setPin((p) => ({ ...p, status: 'published' }));
      }

      setShowPublish(false);
      onPublished?.();
    } catch (e) {
      const errMsg = e.response?.data?.detail || 'Publishing failed';
      setPublishError(errMsg);
      await axios.put(`${API}/api/pin-studio/pins/${pin.id}`, {
        status: 'failed',
        publish_error: errMsg,
      }, getAuth()).catch(() => {});
      setPin((p) => ({ ...p, status: 'failed', publish_error: errMsg }));
    }
    setPublishing(false);
  };

  if (loading || !pin) {
    return (
      <div className="min-h-screen bg-[#F7F5F2] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#B49A88]" />
      </div>
    );
  }

  const selectedLayer = layers.find((l) => l.id === selectedLayerId);
  const angleInfo = ANGLE_LABELS[pin.marketing_angle] || {};

  return (
    <div className="min-h-screen bg-[#F7F5F2]" data-testid="pin-editor">
      {/* Top bar */}
      <div className="bg-white border-b border-stone-100 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-stone-50" data-testid="editor-back-btn">
              <ArrowLeft className="w-4 h-4 text-stone-400" />
            </button>
            <div>
              <h1 className="font-sans font-bold text-stone-800 text-sm">Pin Editor</h1>
              {lastSaved && (
                <p className="font-sans text-[9px] text-stone-400">
                  Last saved {lastSaved.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={undo} disabled={historyIdx <= 0} className="p-2 rounded-lg hover:bg-stone-50 disabled:opacity-30" data-testid="undo-btn">
              <Undo2 className="w-4 h-4 text-stone-500" />
            </button>
            <button onClick={redo} disabled={historyIdx >= history.length - 1} className="p-2 rounded-lg hover:bg-stone-50 disabled:opacity-30" data-testid="redo-btn">
              <Redo2 className="w-4 h-4 text-stone-500" />
            </button>
            <div className="w-px h-5 bg-stone-100 mx-1" />
            <Button
              variant="outline"
              onClick={() => savePin(false)}
              disabled={saving}
              className="rounded-xl px-3 py-2 text-xs font-sans font-semibold"
              data-testid="save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
            </Button>
            <Button
              variant="outline"
              onClick={exportPin}
              disabled={exporting}
              className="rounded-xl px-3 py-2 text-xs font-sans font-semibold"
              data-testid="download-btn"
            >
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} Download
            </Button>
            <Button
              onClick={() => setShowPublish(true)}
              className="bg-[#E60023] hover:bg-[#cc001f] text-white rounded-xl px-4 py-2 text-xs font-sans font-semibold"
              data-testid="publish-btn"
            >
              <Send className="w-3.5 h-3.5 mr-1.5" /> Publish
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex gap-5">
          {/* Left: Preview */}
          <div className="flex-shrink-0" style={{ width: PIN_WIDTH * SCALE + 24 }}>
            <div className="sticky top-20">
              <div className="bg-white rounded-2xl border border-stone-100 p-3">
                <PinPreview layers={layers} scale={SCALE} />
              </div>
              {/* Layout switcher */}
              <div className="mt-4">
                <p className="font-sans text-[10px] font-semibold text-stone-500 mb-2">Layout & Design</p>
                <TemplatePicker
                  productCategory={pin?.source_product_title?.toLowerCase() || ''}
                  marketingAngle={pin?.marketing_angle || ''}
                  selectedId={pin?.template_id || 'editorial_luxury'}
                  onSelect={changeLayout}
                />
              </div>
              {/* Palette switcher */}
              <div className="mt-3">
                <p className="font-sans text-[10px] font-semibold text-stone-500 mb-2">Fargepalett</p>
                <div className="flex gap-1.5 flex-wrap">
                  {PIN_PALETTES.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => changePalette(p)}
                      className={`w-7 h-7 rounded-lg border-2 transition-all ${palette.id === p.id ? 'border-stone-900 scale-110' : 'border-transparent hover:border-stone-300'}`}
                      style={{ background: `linear-gradient(135deg, ${p.bg} 50%, ${p.accent} 50%)` }}
                      title={p.id}
                    />
                  ))}
                </div>
              </div>
              {/* Score */}
              <div className="mt-4">
                <PinScore pin={pin} />
              </div>
            </div>
          </div>

          {/* Right: Properties panel */}
          <div className="flex-1 space-y-4 min-w-0">
            {/* Pin content */}
            <div className="bg-white rounded-2xl border border-stone-100 p-5 space-y-4">
              <h3 className="font-sans font-bold text-stone-700 text-xs flex items-center gap-2">
                <Type className="w-3.5 h-3.5 text-[#E60023]" /> Pin-innhold
                {angleInfo.label && (
                  <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full" style={{ background: (angleInfo.color || '#888') + '15', color: angleInfo.color }}>
                    {angleInfo.label}
                  </span>
                )}
              </h3>
              <div>
                <label className="font-sans text-[10px] font-semibold text-stone-500 mb-1 block">Headline</label>
                <input
                  data-testid="edit-headline"
                  value={pin.headline || ''}
                  onChange={(e) => updatePin('headline', e.target.value)}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm font-serif font-bold text-stone-700 focus:outline-none focus:border-[#B49A88]"
                />
              </div>
              <div>
                <label className="font-sans text-[10px] font-semibold text-stone-500 mb-1 block">Subheadline</label>
                <input
                  data-testid="edit-subheadline"
                  value={pin.subheadline || ''}
                  onChange={(e) => updatePin('subheadline', e.target.value)}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 focus:outline-none focus:border-[#B49A88]"
                />
              </div>
              <div>
                <label className="font-sans text-[10px] font-semibold text-stone-500 mb-1 block">CTA</label>
                <input
                  data-testid="edit-cta"
                  value={pin.cta || ''}
                  onChange={(e) => updatePin('cta', e.target.value)}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 focus:outline-none focus:border-[#B49A88]"
                />
              </div>
            </div>

            {/* Pinterest SEO fields */}
            <div className="bg-white rounded-2xl border border-stone-100 p-5 space-y-4">
              <h3 className="font-sans font-bold text-stone-700 text-xs flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#E60023]" /> Pinterest SEO
              </h3>
              <div>
                <label className="font-sans text-[10px] font-semibold text-stone-500 mb-1 block">Pinterest Title</label>
                <input
                  data-testid="edit-pinterest-title"
                  value={pin.pinterest_title || ''}
                  onChange={(e) => updatePin('pinterest_title', e.target.value)}
                  maxLength={100}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 focus:outline-none focus:border-[#B49A88]"
                />
                <p className="font-sans text-[9px] text-stone-400 mt-0.5">{(pin.pinterest_title || '').length}/100 tegn</p>
              </div>
              <div>
                <label className="font-sans text-[10px] font-semibold text-stone-500 mb-1 block">Pinterest Description</label>
                <textarea
                  data-testid="edit-pinterest-desc"
                  value={pin.pinterest_description || ''}
                  onChange={(e) => updatePin('pinterest_description', e.target.value)}
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 focus:outline-none focus:border-[#B49A88] resize-none"
                />
                <p className="font-sans text-[9px] text-stone-400 mt-0.5">{(pin.pinterest_description || '').length}/500 tegn</p>
              </div>
              <div>
                <label className="font-sans text-[10px] font-semibold text-stone-500 mb-1 block">Keywords (kommaseparert)</label>
                <input
                  data-testid="edit-keywords"
                  value={(pin.pinterest_keywords || []).join(', ')}
                  onChange={(e) => updatePin('pinterest_keywords', e.target.value.split(',').map((k) => k.trim()).filter(Boolean))}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 focus:outline-none focus:border-[#B49A88]"
                />
              </div>
              <div>
                <label className="font-sans text-[10px] font-semibold text-stone-500 mb-1 block">Alt Text</label>
                <input
                  data-testid="edit-alt-text"
                  value={pin.pinterest_alt_text || ''}
                  onChange={(e) => updatePin('pinterest_alt_text', e.target.value)}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 focus:outline-none focus:border-[#B49A88]"
                />
              </div>
            </div>

            {/* Destination URL */}
            <div className="bg-white rounded-2xl border border-stone-100 p-5 space-y-3">
              <h3 className="font-sans font-bold text-stone-700 text-xs flex items-center gap-2">
                <Link2 className="w-3.5 h-3.5 text-[#E60023]" /> Destination URL
              </h3>
              <input
                data-testid="edit-destination-url"
                value={pin.destination_url || ''}
                onChange={(e) => updatePin('destination_url', e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 focus:outline-none focus:border-[#B49A88]"
              />
              <p className="font-sans text-[9px] text-stone-400">UTM-parametere legges til automatisk ved publisering (utm_source=pinterest)</p>
            </div>

            {/* Board selection */}
            {boards.length > 0 && (
              <div className="bg-white rounded-2xl border border-stone-100 p-5 space-y-3">
                <h3 className="font-sans font-bold text-stone-700 text-xs">Select Board</h3>
                <select
                  data-testid="board-select"
                  value={pin.board_id || ''}
                  onChange={(e) => {
                    const board = boards.find((b) => b.id === e.target.value);
                    updatePin('board_id', e.target.value);
                    if (board) updatePin('board_name', board.name);
                  }}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 bg-white focus:outline-none focus:border-[#B49A88]"
                >
                  <option value="">Select board...</option>
                  {boards.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Publish modal */}
      {showPublish && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="publish-modal">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-serif font-bold text-stone-800 text-lg">Publish Pin</h3>
              <button onClick={() => { setShowPublish(false); setPublishError(''); }} className="p-1 rounded-lg hover:bg-stone-50">
                <X className="w-4 h-4 text-stone-400" />
              </button>
            </div>

            {!pinterestStatus?.connected ? (
              <div className="text-center py-6">
                <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                <p className="font-sans text-sm text-stone-600 mb-1">Pinterest not connected</p>
                <p className="font-sans text-xs text-stone-400 mb-4">You can save and download Pins, but need a connection to publish.</p>
                <Button variant="outline" onClick={() => setShowPublish(false)} className="rounded-xl">
                  Close
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl">
                  <div className="w-12 h-16 bg-stone-200 rounded-lg flex items-center justify-center">
                    <Image className="w-5 h-5 text-stone-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-sans font-semibold text-stone-700 text-xs truncate">{pin.pinterest_title || pin.headline}</p>
                    <p className="font-sans text-[10px] text-stone-400">{pin.board_name || 'None board selected'}</p>
                  </div>
                </div>

                {!pin.board_id && (
                  <div className="p-3 bg-amber-50 rounded-xl flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <p className="font-sans text-xs text-amber-700">Select a board in the editor before publishing</p>
                  </div>
                )}

                {/* Schedule option */}
                <div className="border border-stone-100 rounded-xl p-4 space-y-3">
                  <p className="font-sans font-semibold text-stone-700 text-xs flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5" /> Planlegg (optional)
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      className="px-3 py-2 border border-stone-200 rounded-lg text-xs font-sans"
                      data-testid="schedule-date"
                    />
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className="px-3 py-2 border border-stone-200 rounded-lg text-xs font-sans"
                      data-testid="schedule-time"
                    />
                  </div>
                </div>

                {publishError && (
                  <div className="p-3 bg-red-50 rounded-xl flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-sans text-xs text-red-700">{publishError}</p>
                      <button onClick={() => handlePublish(!!scheduleDate)} className="font-sans text-xs font-semibold text-red-600 hover:underline mt-1">
                        Try again
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  {scheduleDate && scheduleTime ? (
                    <Button
                      onClick={() => handlePublish(true)}
                      disabled={publishing || !pin.board_id}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-4 text-xs font-sans font-semibold disabled:opacity-50"
                      data-testid="schedule-btn"
                    >
                      {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
                      Planlegg
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handlePublish(false)}
                      disabled={publishing || !pin.board_id}
                      className="flex-1 bg-[#E60023] hover:bg-[#cc001f] text-white rounded-xl py-4 text-xs font-sans font-semibold disabled:opacity-50"
                      data-testid="publish-now-btn"
                    >
                      {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Publish now
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Canvas helpers
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(ctx, text, maxWidth) {
  const lines = [];
  const paragraphs = text.split('\n');
  for (const paragraph of paragraphs) {
    const words = paragraph.split(' ');
    let line = '';
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    lines.push(line);
  }
  return lines;
}

export default PinEditor;
